/**
 * Password decision page — /password-decision (AUTH-250, AUTH-257)
 *
 * Reached from either way an unverified account that already has a password
 * gets verified: a Google (OAuth) sign-in, or a click on the email
 * verification link. Whoever set that password never proved they own the
 * mailbox, so verification is held here — on its own screen, before any
 * session and before 2FA — until the owner replaces the password or, when the
 * account has another way in, removes it. The pending decision lives in an
 * httpOnly cookie the server set on the redirect; this page never sees a token.
 *
 * An email-link hold (`canConfirm`) first asks for the EXISTING password,
 * because a user who signed up on one device and clicked the link on another
 * is the common case, not a warning sign (mfaf39d). Knowing the password
 * finishes verification with nothing changed. Only when the user does not know
 * it, or says so, does the page show the decision below and its "we can't tell
 * who chose it" copy. The OAuth hold goes straight to the decision.
 *
 * "Remove" is rendered only when the server reports `canRemove` (a linked
 * provider exists). Absent, never disabled: removing the only credential would
 * leave the account with no way to sign in, and the server refuses it anyway.
 *
 * On success the server issues the session and returns the callback URL the
 * sign-in started with, so the gateway's "/" decision tree (2FA, redirect_uri)
 * runs exactly as it does after any other sign-in.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { Button } from "@better-bookkeeping/ui/button";
import { Label } from "@better-bookkeeping/ui/label";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { authClient } from "@/lib/auth-client";
import { PasswordStrengthMeter } from "@/components/password-strength-meter";
import { PasswordInput } from "@better-bookkeeping/ui/password-input";

export const Route = createFileRoute("/password-decision")({
  component: PasswordDecisionPage,
});

type Status =
  | {
      pending: true;
      email: string;
      source?: "oauth" | "email_link";
      canConfirm?: boolean;
      canRemove?: boolean;
      provider?: string | null;
    }
  | { pending: false };

const PROVIDER_LABELS: Record<string, string> = { google: "Google", github: "GitHub" };

function providerLabel(provider: string | null | undefined): string {
  return (provider && PROVIDER_LABELS[provider]) || "your linked account";
}
type ResolveBody = { action: "set"; newPassword: string } | { action: "remove" };

async function confirmExistingPassword(password: string) {
  return authClient.$fetch<{ redirectTo: string }>("/password-decision/confirm", {
    method: "POST",
    body: { password },
  });
}

async function resolveDecision(body: ResolveBody) {
  return authClient.$fetch<{ redirectTo: string }>("/password-decision/resolve", {
    method: "POST",
    body,
  });
}

function describeError(error: { status?: number; message?: string } | null): string {
  if (!error) return "Something went wrong. Please try again.";
  if (error.status === 429 || error.message?.includes("429")) {
    return "Too many tries. Take a breath and try again in a minute.";
  }
  if (error.status === 400 && error.message?.toLowerCase().includes("expired")) {
    return "This step has expired. Start again from sign-in or your verification email.";
  }
  return error.message || "We couldn't save that. Please try again.";
}

export function PasswordDecisionPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  // Set when the user says they do not know the existing password.
  const [declined, setDeclined] = useState(false);
  const [existingPassword, setExistingPassword] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authClient
      .$fetch<Status>("/password-decision/status", { method: "GET" })
      .then(({ data }) => {
        if (!cancelled) setStatus(data ?? { pending: false });
      })
      .catch(() => {
        if (!cancelled) setStatus({ pending: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const finish = async (body: ResolveBody) => {
    setError(null);
    const { data, error: resolveError } = await resolveDecision(body);
    if (resolveError || !data?.redirectTo) {
      setError(describeError(resolveError));
      return;
    }
    // Full navigation: the new session cookie must be sent with the gateway's
    // "/" request, which then runs 2FA and the redirect_uri hand-off.
    window.location.href = data.redirectTo;
  };

  const form = useForm({
    defaultValues: { password: "", confirmPassword: "" },
    onSubmit: async ({ value }) => {
      if (value.password !== value.confirmPassword) {
        setError("Those don't match. Try retyping the second one.");
        return;
      }
      await finish({ action: "set", newPassword: value.password });
    },
  });

  if (status === null) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="password-decision-loading">
        Loading…
      </p>
    );
  }

  if (!status.pending) {
    return (
      <div className="space-y-6">
        <div className="space-y-1.5">
          <h2
            className="text-2xl font-semibold tracking-tight"
            data-testid="password-decision-expired-title"
          >
            This step has expired
          </h2>
          <p className="text-sm text-muted-foreground">
            Start again from sign-in or your verification email.
          </p>
        </div>
        <Button asChild className="w-full" data-testid="password-decision-restart-link">
          <a href="/login">Back to sign in</a>
        </Button>
      </div>
    );
  }

  // Strict: an absent field hides the option rather than offering it.
  const canRemove = status.canRemove === true;
  const via = providerLabel(status.provider);

  if (status.canConfirm === true && !declined) {
    const submitExisting = async () => {
      setError(null);
      setConfirming(true);
      try {
        const { data, error: confirmError } = await confirmExistingPassword(existingPassword);
        if (confirmError || !data?.redirectTo) {
          // Generic on purpose: never hint at how close a guess was.
          setError(
            confirmError?.status === 401
              ? "That password didn't work. Try again, or choose “I don't know this password”."
              : describeError(confirmError),
          );
          return;
        }
        window.location.href = data.redirectTo;
      } finally {
        setConfirming(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="space-y-1.5">
          <h2
            className="text-2xl font-semibold tracking-tight"
            data-testid="password-decision-challenge-title"
          >
            Enter your password
          </h2>
          <p
            className="text-sm text-muted-foreground"
            data-testid="password-decision-challenge-explanation"
          >
            To finish confirming <span className="font-medium">{status.email}</span>, enter the
            password you chose when you signed up.
          </p>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="password-decision-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitExisting();
          }}
          className="space-y-4"
          data-testid="password-decision-challenge-form"
        >
          <div className="space-y-2">
            <Label htmlFor="existingPassword">Password</Label>
            <PasswordInput
              id="existingPassword"
              autoComplete="current-password"
              value={existingPassword}
              onChange={(e) => setExistingPassword(e.target.value)}
              autoFocus
              required
              data-testid="password-decision-challenge-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={confirming || existingPassword.length === 0}
            className="w-full"
            data-testid="password-decision-challenge-button"
          >
            {confirming ? "Checking…" : "Continue"}
          </Button>
        </form>

        <Button
          type="button"
          variant="link"
          className="w-full"
          disabled={confirming}
          data-testid="password-decision-challenge-unknown-button"
          onClick={() => {
            setError(null);
            setDeclined(true);
          }}
        >
          I don't know this password
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight" data-testid="password-decision-title">
          Secure your password first
        </h2>
        <p className="text-sm text-muted-foreground" data-testid="password-decision-explanation">
          A password was set for <span className="font-medium">{status.email}</span> before the
          email address was confirmed, so we can't tell who chose it.{" "}
          {canRemove
            ? `Set a new password, or remove it and sign in with ${via} only.`
            : "Set a new password to continue."}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="password-decision-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="space-y-4"
        data-testid="password-decision-set-form"
      >
        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <form.Field
            name="password"
            validators={{ onChange: z.string().min(12, "Use at least 12 characters.") }}
            children={(field) => (
              <div className="space-y-2">
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="At least 12 characters"
                  autoFocus
                  required
                  data-testid="password-decision-new-password-input"
                />
                <PasswordStrengthMeter
                  value={field.state.value}
                  data-testid="password-decision-password-strength-section"
                />
                {field.state.meta.errors.length > 0 && field.state.value.length > 0 && (
                  <p
                    className="text-sm text-destructive"
                    data-testid="password-decision-password-error"
                  >
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <form.Field
            name="confirmPassword"
            validators={{ onChange: z.string().min(1, "Please confirm your password") }}
            children={(field) => (
              <PasswordInput
                id="confirmPassword"
                autoComplete="new-password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="Re-enter your password"
                required
                data-testid="password-decision-confirm-password-input"
              />
            )}
          />
        </div>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting || removing}
              className="w-full"
              data-testid="password-decision-set-button"
            >
              {isSubmitting ? "Saving…" : "Set new password and continue"}
            </Button>
          )}
        />
      </form>

      {canRemove && (
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={removing}
            data-testid="password-decision-remove-button"
            onClick={async () => {
              setRemoving(true);
              try {
                await finish({ action: "remove" });
              } finally {
                setRemoving(false);
              }
            }}
          >
            {removing ? "Removing…" : `Remove the password and use ${via} only`}
          </Button>
        </>
      )}
    </div>
  );
}
